import { isNotEmpty } from '@dealstack/shared/lib/isNotEmpty.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useMutation, useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';
import {
	createGdwtCustomDetailDefinition,
	deleteGdwtCustomDetailsDefinitions,
	getGdwtCustomDetailsDefinitions,
	updateGdwtCustomDetailsDefinitions,
} from '@dealstack/frontend/shared/dataExchange/services/god.service.ts';
import { useState } from 'react';
import { ManageCustomDetailsDefinitionsForm } from '@dealstack/frontend/admin/pages/Manage/common/ManageCustomDetailsDefinitionsModal/types';

export const useDocumentCustomDetailsDefinitions = (templateId: string) => {
	const geCustomDetailsDefinitionsRequest = useQuery({
		queryKey: [
			API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_CUSTOM_DETAILS_DEFINITIONS_GET,
			{ id: templateId },
		],

		queryFn: ({ signal }) =>
			getGdwtCustomDetailsDefinitions(
				API_PATHS.GODMODE_GENERATABLE_DOCUMENTS_TEMPLATE_ID_CUSTOM_DETAILS_DEFINITIONS_GET,
				{ id: templateId },
				signal
			),
	});

	const customDetailsDefinitions = geCustomDetailsDefinitionsRequest?.data;

	const createCustomDetailsDefinitionsRequest = useMutation({
		mutationFn: createGdwtCustomDetailDefinition,
	});
	const updateCustomDetailsDefinitionsRequest = useMutation({
		mutationFn: updateGdwtCustomDetailsDefinitions,
	});
	const deleteCustomDetailsDefinitionsRequest = useMutation({
		mutationFn: deleteGdwtCustomDetailsDefinitions,
	});

	const [showManageCustomDetailsDefinitionsModal, setShowManageCustomDetailsDefinitionsModal] =
		useState(false);

	const onCustomDetailsDefinitionsSubmit = async (
		formValues: ManageCustomDetailsDefinitionsForm
	) => {
		const formCustomDetailDefinitions = formValues.customDefinitions;

		let isAnyFailed = false;
		const definitionsToCreate = formCustomDetailDefinitions
			.filter((def) => !!def.localId)
			.map((def) => ({
				id: def.id as string,
				name: def.name,
				instruction: def.instruction,
				entityType: def.entityType,
			}));
		const definitionsToUpdate = formCustomDetailDefinitions
			.filter((def) => {
				const originalDefinition = customDetailsDefinitions?.find(
					(initialDef) => initialDef.id === def.id
				);
				return (
					!!originalDefinition?.id &&
					(originalDefinition.name !== def.name ||
						originalDefinition.instruction !== def.instruction)
				);
			})
			.map((def) => ({
				id: def.id as string,
				name: def.name,
				instruction: def.instruction ?? undefined,
				entityType: def.entityType,
			}));
		const definitionsToDelete = customDetailsDefinitions
			?.filter((initialDef) => {
				const localDefinition = formCustomDetailDefinitions.find(
					(def) => initialDef.id === def.id
				);
				return !localDefinition;
			})
			.map((def) => def.id as string);

		await Promise.allSettled([
			isNotEmpty(definitionsToCreate)
				? createCustomDetailsDefinitionsRequest.mutateAsync(
						{
							customDetailsDefinitions: definitionsToCreate,
							templateId,
						},
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
			isNotEmpty(definitionsToUpdate)
				? updateCustomDetailsDefinitionsRequest.mutateAsync(
						{ customDetailsDefinitions: definitionsToUpdate },
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
			isNotEmpty(definitionsToDelete)
				? deleteCustomDetailsDefinitionsRequest.mutateAsync(
						{ customDetailsDefinitionsIds: definitionsToDelete },
						{
							onError: () => {
								isAnyFailed = true;
							},
						}
					)
				: null,
		]);

		if (isAnyFailed) {
			showToastV2({ state: 'error', message: 'Failed to save data' });
			return;
		}

		showToastV2({ message: 'Saved' });
		await geCustomDetailsDefinitionsRequest.refetch();
		setShowManageCustomDetailsDefinitionsModal(false);
	};

	return {
		onCustomDetailsDefinitionsSubmit,
		customDetailsDefinitions,
		showManageCustomDetailsDefinitionsModal,
		setShowManageCustomDetailsDefinitionsModal,
	};
};
